import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';

import { CELL_SIZE, DEFAULT_SEARCH_RADIUS_CELLS, cellKey, nearestFreeCell, occupiedCellKeys } from '../core/GridSnap';
import type { Point3 } from '../core/GridSnap';
import type { RoomState } from '../room/RoomState';
import type { RoomView } from '../room/RoomView';
import { travelPositionAt } from './IndicatorTravel';
import type { PlacementManager } from './PlacementManager';
import type { PossessionManager } from './PossessionManager';

/**
 * Marks the grid cell a carried or held shape would snap into if released right now.
 *
 * The owner's own framing, after the floor grid toggle shipped and turned out not to be the
 * answer: *"the floor grid isn't that helpful... paint a half-transparent ball of 3cm at the
 * center point of the nearest node while you hold something, so it's clear where it will end
 * up"* — "node" there means grid cell, the one `GridSnap.nearestFreeCell` would choose.
 *
 * Calls `nearestFreeCell` directly rather than re-deriving its arithmetic — the whole point is
 * that this points at *the same* cell the real snap on release/commit would land in, occupancy
 * included, not merely a plausible-looking one nearby. `PlacementManager.commit` and
 * `PossessionManager.release` both build their occupancy sets the same way (`occupiedCellKeys`
 * over `state.nodes`, excluding the node in hand); this mirrors that exactly rather than
 * calling into either of them, since neither exposes the search as a side-effect-free query.
 *
 * One mesh, created once and moved — not created per frame (CLAUDE.md's own leak notes: count
 * materials separately from meshes, and `TransformNode.dispose()`'s cascade does not free a
 * material on its own, so this disposes both explicitly). Never pickable, and never added to
 * `SceneRoot.floorMeshes` — it has nothing to do with teleportation.
 *
 * Second iteration, deliberately not built here: the owner also floated showing every free
 * cell within a 1m sphere, and set it aside himself — "might get a bit confusing so could be
 * saved for attempt number two if the single node doesn't help." This is attempt one, a single
 * indicator only.
 *
 * Reported from a headset once this attempt one existed: the indicator jumped instantly between
 * cells, which made it hard to tell which direction it had moved. The owner: *"they need to
 * either visibly travel between spots or at the very least fade a bit when they move. I prefer
 * them moving quickly to the next spot so it's easier to keep track of where it will land when
 * you move it."* `tick` now slides the mesh toward a new target over `TRAVEL_DURATION_MS`
 * (`IndicatorTravel.ts` has the pure shaping and the reasoning for the duration/easing chosen)
 * rather than setting `mesh.position` straight to it. Two cases handled deliberately rather than
 * falling out of the general case by luck: the indicator's first appearance has nothing to slide
 * from, so it snaps straight to the first target instead of travelling in from wherever the mesh
 * happened to be left disabled; and retargeting mid-slide continues from wherever the indicator
 * currently is, not from the old target it never reached — `currentPosition()` below always
 * reads the position actually being rendered this frame, not the slide's nominal endpoint.
 */

/** ~3cm diameter, the owner's own figure. */
const INDICATOR_DIAMETER = 0.03;

const INDICATOR_COLOR = '#FFFFFF';
const INDICATOR_ALPHA = 0.4;

/** A slide currently in progress, or just finished — `startedAtMs` is a `performance.now()`
 *  timestamp, the same clock `RoomView`'s own fade animations tick against. */
interface Travel {
  readonly from: Point3;
  readonly to: Point3;
  readonly startedAtMs: number;
}

// spec: SPEC-069
export class SnapIndicator {
  private readonly mesh: Mesh;

  /** Whether the indicator was showing as of the previous tick — the one thing that decides
   *  whether a new target should be slid to or snapped to instantly (see the class doc comment,
   *  "the indicator's first appearance"). */
  private wasVisible = false;

  /** The slide currently carrying the mesh toward `targetCellKey`, or `null` once it has
   *  arrived — a finished slide is discarded rather than kept at `done: true`, so a later
   *  retarget away from the same cell has nothing stale to read `from` off other than the
   *  mesh's own current (== target) position. */
  private travel: Travel | null = null;

  /** The grid cell the indicator is currently sliding toward or resting on, as the same string
   *  key `GridSnap.cellKey` produces — compared against the newest `nearestFreeCell` result
   *  every tick to tell "still the same target" from "the target moved", since two different
   *  `Point3` objects representing the identical cell centre are not reliably `===` or even
   *  bit-identical without redoing that arithmetic here. */
  private targetCellKey: string | null = null;

  constructor(
    private readonly scene: Scene,
    roomOrigin: TransformNode,
    private readonly state: RoomState,
    private readonly view: RoomView,
    private readonly placement: PlacementManager,
    private readonly possession: PossessionManager,
  ) {
    this.mesh = CreateSphere('snap-indicator', { diameter: INDICATOR_DIAMETER, segments: 12 }, scene);
    this.mesh.parent = roomOrigin;
    this.mesh.isPickable = false;

    const material = new StandardMaterial('snap-indicator', scene);
    const color = Color3.FromHexString(INDICATOR_COLOR);
    material.diffuseColor = color;
    material.emissiveColor = color.scale(0.5);
    material.specularColor = Color3.Black();
    material.alpha = INDICATOR_ALPHA;
    this.mesh.material = material;

    this.mesh.setEnabled(false);
  }

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick());

    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  private tick(): void {
    const nodeId = this.placement.carriedNodeId ?? this.possession.heldNodeId;

    if (nodeId === null) {
      this.hide();
      return;
    }

    const mesh = this.view.meshFor(nodeId);

    if (!mesh) {
      this.hide();
      return;
    }

    // `mesh.position` is a plain field written synchronously by PlacementManager.tick /
    // PossessionManager.trackHeld earlier this same frame (AuthoringController.start registers
    // this after both) — not a world-matrix-derived read, so there is no render-id staleness
    // for `computeWorldMatrix(true)` to fix here (contrast `isWithinGrabReach`'s
    // `boundingSphere.centerWorld`, or `labelAnchorFor`'s bounding box, both of which do need
    // it). Both the held mesh and this indicator share the same parent (`roomOrigin`, which
    // never itself moves), so local space is also exactly the space `occupiedCellKeys` and
    // `nearestFreeCell` already work in below — the same space `PlacementManager`/
    // `PossessionManager` snap against.
    const desired: Vector3 = mesh.position;

    // Same occupancy construction `PlacementManager.commit` / `PossessionManager.release` use:
    // every other node's last-committed position, excluding the one in hand — its own prior
    // cell must not read as "occupied by itself".
    const occupied = occupiedCellKeys(
      [...this.state.nodes.values()]
        .filter((candidate) => candidate.id !== nodeId)
        .map((candidate) => candidate.transform.position),
      CELL_SIZE,
    );

    const nearest = nearestFreeCell(
      { x: desired.x, y: desired.y, z: desired.z },
      occupied,
      CELL_SIZE,
      DEFAULT_SEARCH_RADIUS_CELLS,
    );

    // Honest miss, the same rule the real snap uses on release: nothing free nearby means the
    // shape would land unsnapped, so the indicator must not claim a cell that will not be used.
    if (nearest === null) {
      this.hide();
      return;
    }

    const target = nearest.position;
    const targetKey = cellKey(nearest.index);

    // First appearance: nothing to slide from, so land on the target immediately rather than
    // travelling in from wherever the (disabled, unwatched) mesh was last left.
    if (!this.wasVisible) {
      this.mesh.position.set(target.x, target.y, target.z);
      this.travel = null;
      this.targetCellKey = targetKey;
      this.wasVisible = true;
      this.mesh.setEnabled(true);
      return;
    }

    if (this.targetCellKey !== targetKey) {
      // Retargeting — continue from wherever the indicator is *right now*, mid-slide or at
      // rest, never from the old target it may never have reached.
      const from: Point3 = this.travel
        ? travelPositionAt(performance.now() - this.travel.startedAtMs, this.travel.from, this.travel.to).position
        : { x: this.mesh.position.x, y: this.mesh.position.y, z: this.mesh.position.z };

      this.travel = { from, to: target, startedAtMs: performance.now() };
      this.targetCellKey = targetKey;
    }

    if (this.travel !== null) {
      const step = travelPositionAt(performance.now() - this.travel.startedAtMs, this.travel.from, this.travel.to);
      this.mesh.position.set(step.position.x, step.position.y, step.position.z);

      if (step.done) {
        this.travel = null;
      }
    }

    this.mesh.setEnabled(true);
  }

  private hide(): void {
    this.mesh.setEnabled(false);
    this.wasVisible = false;
    this.travel = null;
    this.targetCellKey = null;
  }

  dispose(): void {
    this.mesh.material?.dispose();
    this.mesh.dispose();
  }
}
